import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import API from './api/axiosConfig';

import Navbar from './components/Navbar';
import { AuthProvider, useAuth } from './context/AuthContext';

import Login from './features/auth/Login';
import Register from './features/auth/Register';
import ForceChangePasswordPage from './features/auth/ForceChangePasswordPage';
import ProtectedRoute from './features/auth/ProtectedRoute';

import Profile from './features/profile/Profile';
import ProfileSettings from './features/profile/ProfileSettings';
import TestPage from './features/tests/TestPage';
import QuestionsManager from './features/questions/QuestionsManager';
import CalendarPage from './features/calendar/CalendarPage';
import MessagesPage from './features/messages/MessagesPage';
import AnalyticsPage from './features/analytics/AnalyticsPage';
import UserActivityDetail from './features/analytics/UserActivityDetail';
import TeacherDashboard from './features/teacher/TeacherDashboard';
import StudentDashboard from './features/student/StudentDashboard';
import TestConstructor from './pages/TestConstructor';
import AssignedTestPage from './pages/AssignedTestPage';

// === ДОБАВЛЕННЫЕ ИМПОРТЫ ДЛЯ НОВЫХ СТРАНИЦ ===
import GraduationExamSetup from './pages/GraduationExamSetup';
import CompositeTestPage from './pages/CompositeTestPage';

// ==========================================
// КОМПОНЕНТ АКТИВАЦИИ АККАУНТА
// ==========================================
function ActivateAccount() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState('Активация вашего профиля в системе...');
  const [isSuccess, setIsSuccess] = useState(null);

  useEffect(() => {
    API.get(`/api/activate/${uid}/${token}/`)
      .then((res) => {
        setIsSuccess(true);
        setStatusMessage('Учетная запись успешно активирована! Сейчас вы будете перенаправлены на страницу входа.');
        setTimeout(() => navigate('/login'), 4000);
      })
      .catch((err) => {
        setIsSuccess(false);
        const serverError = err.response?.data?.error || 'Ссылка недействительна или срок её действия истек.';
        setStatusMessage(`Ошибка активации: ${serverError}`);
      });
  }, [uid, token, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-md w-full bg-white p-8 rounded-3xl shadow-xl border border-slate-100 text-center">
        <div className="mb-6">
          {isSuccess === null && <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>}
          {isSuccess === true && <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto text-2xl font-bold">✓</div>}
          {isSuccess === false && <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto text-2xl font-bold">✕</div>}
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-3">Верификация аккаунта</h2>
        <p className={`text-sm font-medium ${isSuccess === true ? 'text-green-600' : isSuccess === false ? 'text-red-600' : 'text-slate-500'}`}>
          {statusMessage}
        </p>
        {isSuccess === false && (
          <Link to="/register" className="mt-6 inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-xl transition-all">
            Повторить регистрацию
          </Link>
        )}
      </div>
    </div>
  );
}

// ==========================================
// НОВАЯ СТРАНИЦА: ОБЩАЯ БИБЛИОТЕКА ПРЕДМЕТОВ
// ==========================================
function PublicLibrary() {
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState(5);
  const { isAuthenticated, loading } = useAuth(); 

  const subjectStyles = {
    'Математика': { icon: '📐', bg: 'bg-blue-100', text: 'text-blue-600' },
    'Химия': { icon: '🧪', bg: 'bg-emerald-100', text: 'text-emerald-600' },
    'Биология': { icon: '🧬', bg: 'bg-green-100', text: 'text-green-600' },
    'Литература': { icon: '📚', bg: 'bg-amber-100', text: 'text-amber-600' },
    'Русский язык': { icon: '🖋️', bg: 'bg-red-100', text: 'text-red-600' },
    'Азербайджанский язык': { icon: '🇦🇿', bg: 'bg-teal-100', text: 'text-teal-600' },
    'Английский язык': { icon: '🇬🇧', bg: 'bg-indigo-100', text: 'text-indigo-600' },
    'Немецкий язык': { icon: '🇩🇪', bg: 'bg-orange-100', text: 'text-orange-600' },
    'default': { icon: '📘', bg: 'bg-slate-200', text: 'text-slate-600' }
  };

  useEffect(() => {
    if (loading) return;
    API.get('/api/subjects/', { params: { class_level: selectedClass } })
      .then(res => setSubjects(Array.isArray(res.data) ? res.data : []))
      .catch(err => setSubjects([]));
  }, [isAuthenticated, selectedClass, loading]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20 pt-10">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 px-4 sm:px-6 lg:px-8">
        
        {/* ЛЕВАЯ КОЛОНКА (ПРЕДМЕТЫ) */}
        <div className="flex-1">
          <div className="text-center lg:text-left mb-8">
            <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-2">Общая библиотека</h1>
            <p className="text-base text-slate-500 font-medium">Выберите класс и предмет для свободной тренировки</p>
          </div>

          <div className="flex justify-center lg:justify-start mb-8">
            <div className="inline-flex bg-slate-200/60 p-1 rounded-xl shadow-inner border border-slate-200 overflow-x-auto max-w-full">
              {[5, 6, 7, 8, 9, 10, 11].map((cls) => (
                <button
                  key={cls}
                  onClick={() => setSelectedClass(cls)}
                  className={`
                    px-5 py-2 text-sm font-bold rounded-lg transition-all duration-200 whitespace-nowrap
                    ${selectedClass === cls 
                      ? 'bg-white text-blue-700 shadow-sm ring-1 ring-black/5' 
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-300/50'}
                  `}
                >
                  {cls} класс
                </button>
              ))}
            </div>
          </div>

          {subjects.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 shadow-sm">
              <span className="text-5xl block mb-4">📭</span>
              <p className="text-slate-500 font-bold text-base mb-4">Для {selectedClass} класса пока нет предметов</p>
              {!isAuthenticated && (
                <Link to="/login" className="text-blue-600 font-black hover:underline uppercase tracking-wider text-sm">Авторизуйтесь в системе →</Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-5">
              {subjects.map((sub) => {
                const style = subjectStyles[sub.name] || subjectStyles['default'];
                return (
                  <div key={sub.id} className="group bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-400 transition-all duration-300 flex flex-col justify-between relative overflow-hidden">
                    <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-20 blur-2xl ${style.bg} pointer-events-none transition-all group-hover:scale-150`}></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl shadow-sm ${style.bg}`}>
                          {style.icon}
                        </div>
                        <div>
                          <h3 className="font-extrabold text-slate-900 text-lg leading-tight mb-1 group-hover:text-blue-700 transition-colors">{sub.name}</h3>
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-2 py-1 rounded-md">
                            Программа {selectedClass} кл.
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500 font-medium mb-6 leading-relaxed">
                        Свободная тренировка по случайным вопросам из общей базы платформы.
                      </p>
                    </div>
                    
                    <Link
                      to={`/test/${sub.id}/${selectedClass}`}
                      className="w-full relative z-10 text-center bg-blue-50 group-hover:bg-blue-600 text-blue-700 group-hover:text-white text-sm font-bold py-3.5 rounded-xl transition-all duration-300 block"
                    >
                      Запустить тест
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ПРАВАЯ КОЛОНКА (САЙДБАР) */}
        <div className="w-full lg:w-80 shrink-0 space-y-6">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-black text-slate-800 text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
              <span>🏆</span> Топ преподавателей
            </h3>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center font-bold text-slate-400">T</div>
                  <div>
                    <p className="text-sm font-bold text-slate-700">Учитель {i}</p>
                    <p className="text-xs text-slate-400 font-medium">Рейтинг: 5.{4 - i} ⭐</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl border border-indigo-100 p-6 shadow-sm h-64 flex flex-col items-center justify-center text-center">
            <span className="text-3xl mb-3">📢</span>
            <h3 className="font-black text-indigo-900 mb-2">Место для рекламы</h3>
            <p className="text-xs font-medium text-indigo-600/70">
              Здесь будет располагаться баннер, новости платформы или полезные ссылки.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

// ==========================================
// ГЛАВНАЯ СТРАНИЦА (КРАСИВЫЙ ЛЕНДИНГ)
// ==========================================
function Home() {
  const navigate = useNavigate();

  // Стейт для реальной статистики из БД
  const [platformStats, setPlatformStats] = useState({
    questions: '121846',
    users: '1800803',
    teachers: '35',
    tests: '3526149'
  });

  useEffect(() => {
    // Получаем реальную статистику
    API.get('/api/platform-stats/')
      .then(res => {
        if (res.data) {
          setPlatformStats(res.data);
        }
      })
      .catch(() => console.log('Не удалось загрузить живую статистику.'));
      
    API.get('/api/hit-homepage/').catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20">
      
      {/* 1. ГЛАВНЫЙ ТИТУЛЬНЫЙ БЛОК (HERO) */}
      <div className="bg-gradient-to-br from-indigo-900 via-blue-900 to-blue-800 text-white pt-20 pb-24 px-4 relative overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>

        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-black mb-6 tracking-tight">
            Будущее образования уже здесь
          </h1>
          <p className="text-lg md:text-xl text-blue-200 mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
            Инновационная платформа для эффективного обучения. Создавайте группы, назначайте тесты и проверяйте знания в режиме реального времени.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div onClick={() => navigate('/teacher-dashboard')} className="group bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md p-8 rounded-3xl cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
              <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">👨‍🏫</div>
              <h3 className="text-2xl font-black mb-2 text-white">Для Учителей</h3>
              <p className="text-blue-200 text-sm font-medium">Создавайте классы, шаблоны тестов и отслеживайте успеваемость в глубокой аналитике.</p>
            </div>

            <div onClick={() => navigate('/student-dashboard')} className="group bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md p-8 rounded-3xl cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
              <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">🎓</div>
              <h3 className="text-2xl font-black mb-2 text-white">Для Учеников</h3>
              <p className="text-blue-200 text-sm font-medium">Решайте домашние задания, общайтесь в чате класса и следите за своим местом в рейтинге.</p>
            </div>

            <div onClick={() => navigate('/library')} className="group bg-blue-600 hover:bg-blue-500 border border-blue-400 p-8 rounded-3xl cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl shadow-blue-900/50">
              <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">📝</div>
              <h3 className="text-2xl font-black mb-2 text-white">Свободные Тесты</h3>
              <p className="text-blue-100 text-sm font-medium">Хотите просто потренироваться? Переходите в библиотеку и решайте тесты из общего банка.</p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. БЛОК СТАТИСТИКИ */}
      <div className="bg-slate-50 py-20 px-4 border-b border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
              Платформа для<br/>проведения онлайн<br/>тестирований
            </h2>
            <p className="text-slate-500 mb-8 font-medium leading-relaxed max-w-lg mx-auto lg:mx-0">
              Узнай свой уровень подготовки к тестам. Тесты для определения уровня и прогресса учеников. Личный кабинет и детальная статистика для учеников, учителей и руководителей.
            </p>
            <button className="text-blue-600 font-bold hover:text-blue-800 transition-colors flex items-center gap-2 mx-auto lg:mx-0">
              Посмотреть инструкцию <span>→</span>
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
              <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">{platformStats.questions}</div>
              <div className="text-sm font-medium text-slate-500">Вопрос(-ов)</div>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
              <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">{platformStats.users}</div>
              <div className="text-sm font-medium text-slate-500">Пользователя(-ей)</div>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
              <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">{platformStats.teachers}</div>
              <div className="text-sm font-medium text-slate-500">Учителей</div>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow">
              <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">{platformStats.tests}</div>
              <div className="text-sm font-medium text-slate-500">Пройденных тестов</div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. ВИДЫ ТЕСТИРОВАНИЯ */}
      <div className="bg-white py-20 px-4 border-b border-slate-200">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 text-center mb-12">Виды тестирования</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            
            {/* Обычные тесты */}
            <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 hover:shadow-xl transition-all flex flex-col h-full group">
              <div className="flex items-center gap-4 mb-6 border-b border-slate-200 pb-6">
                <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center text-3xl shrink-0 group-hover:scale-110 transition-transform">📘</div>
                <div>
                  <h3 className="font-black text-slate-900 text-lg leading-tight">Обычные тесты</h3>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Для всех классов</p>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 30 Вопрос(-ов)</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 1 предмет</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> Без лимита времени</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> Оценка в %</li>
              </ul>
              <button onClick={() => navigate('/library')} className="w-full bg-sky-400 hover:bg-sky-500 text-white font-bold py-3 rounded-xl transition-colors">Начать тест &gt;</button>
            </div>

            {/* Выпускные экзамены */}
            <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 hover:shadow-xl transition-all flex flex-col h-full group">
              <div className="flex items-center gap-4 mb-6 border-b border-slate-200 pb-6">
                <div className="w-14 h-14 bg-purple-100 text-purple-600 rounded-2xl flex items-center justify-center text-3xl shrink-0 group-hover:scale-110 transition-transform">🎓</div>
                <div>
                  <h3 className="font-black text-slate-900 text-lg leading-tight">Выпускные экзамены</h3>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">9 и 11 классы</p>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 85 Вопрос(-ов)</li> 
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 3 предмета</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 180 минут</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 300 баллов</li>
              </ul>
              <button onClick={() => navigate('/graduation-setup')} className="w-full bg-sky-400 hover:bg-sky-500 text-white font-bold py-3 rounded-xl transition-colors">
                Начать тест &gt;
              </button>
            </div>

            {/* Олимпиада */}
            <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 hover:shadow-xl transition-all flex flex-col h-full group">
              <div className="flex items-center gap-4 mb-6 border-b border-slate-200 pb-6">
                <div className="w-14 h-14 bg-pink-100 text-pink-600 rounded-2xl flex items-center justify-center text-3xl shrink-0 group-hover:scale-110 transition-transform">🏆</div>
                <div>
                  <h3 className="font-black text-slate-900 text-lg leading-tight">Олимпиада</h3>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Подготовка</p>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 60 Вопрос(-ов)</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 1 предмет (Проф.)</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 120 минут</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 100 баллов</li>
              </ul>
              <button onClick={() => navigate('/library')} className="w-full bg-sky-400 hover:bg-sky-500 text-white font-bold py-3 rounded-xl transition-colors">Начать тест &gt;</button>
            </div>

            {/* Вступление в ВУЗы */}
            <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 hover:shadow-xl transition-all flex flex-col h-full group">
              <div className="flex items-center gap-4 mb-6 border-b border-slate-200 pb-6">
                <div className="w-14 h-14 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center text-3xl shrink-0 group-hover:scale-110 transition-transform">🏛️</div>
                <div>
                  <h3 className="font-black text-slate-900 text-lg leading-tight">Вступление в ВУЗы</h3>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Абитуриентам</p>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 150 Вопрос(-ов)</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 5 предметов</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 240 минут</li>
                <li className="flex items-center gap-2 text-sm font-semibold text-slate-600"><span className="text-green-500">✓</span> 700 баллов</li>
              </ul>
              <button onClick={() => navigate('/library')} className="w-full bg-sky-400 hover:bg-sky-500 text-white font-bold py-3 rounded-xl transition-colors">Начать тест &gt;</button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// ОСНОВНОЙ КОМПОНЕНТ ПРИЛОЖЕНИЯ И РОУТИНГ
// ==========================================
function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="flex flex-col min-h-screen relative">
          
          <Navbar />
          
          <div className="flex-grow">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/library" element={<PublicLibrary />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/activate/:uid/:token" element={<ActivateAccount />} />
              <Route path="/force-change-password" element={<ForceChangePasswordPage />} />
              
              <Route path="/test/:subjectId/:classLevel" element={<ProtectedRoute><TestPageWrapper /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
              <Route path="/history" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/questions" element={<ProtectedRoute><QuestionsManager /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/analytics/user/:username" element={<ProtectedRoute><UserActivityDetail /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
              <Route path="/teacher-dashboard" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
              <Route path="/student-dashboard" element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} />
              <Route path="/teacher/constructor" element={<TestConstructor />} />
              <Route path="/assigned-test/:eventId" element={<ProtectedRoute><AssignedTestPage /></ProtectedRoute>} />
              
              {/* НОВЫЕ МАРШРУТЫ */}
              <Route path="/graduation-setup" element={<GraduationExamSetup />} />
              <Route path="/composite-test/:classLevel/:lang" element={<ProtectedRoute><CompositeTestPage /></ProtectedRoute>} />
            </Routes>
          </div>

          {/* ПЛАВАЮЩАЯ КНОПКА WHATSAPP ДЛЯ ВСЕХ СТРАНИЦ */}
          <a 
            href="https://wa.me/994500000000" /* ЗАМЕНИТЕ НА ВАШ НОМЕР */
            target="_blank" 
            rel="noopener noreferrer" 
            className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform z-50"
            title="Написать в WhatsApp"
          >
            <svg viewBox="0 0 32 32" className="w-8 h-8 fill-current" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.05 2.5a13.55 13.55 0 0 0-11.53 20.66L2.5 29.5l6.5-1.99A13.55 13.55 0 1 0 16.05 2.5zm0 24.84a11.3 11.3 0 0 1-5.75-1.57l-.42-.24-4.26 1.3 1.15-4.14-.27-.43a11.28 11.28 0 1 1 9.55 5.08zm6.2-7.66c-.34-.17-2-.99-2.31-1.1s-.54-.17-.76.17-.88 1.1-.1 1.33c-.2.22-.5.35-1.07.06-.58-.28-1.43-.65-2.73-1.8-1-1.01-1.68-2.26-1.88-2.6s-.02-.53.15-.7c.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6s-.77-1.84-1.05-2.52c-.27-.66-.55-.57-.76-.58h-.65c-.23 0-.6.09-.91.43s-1.2 1.16-1.2 2.84 1.22 3.3 1.4 3.53c.17.23 2.4 3.66 5.82 5.08 3.42 1.42 3.42.95 4.05.89s2-.82 2.28-1.6.28-1.45.2-1.6z"/>
            </svg>
          </a>

        </div>
      </Router>
    </AuthProvider>
  );
}

function TestPageWrapper() {
  const { subjectId, classLevel } = useParams();
  return <TestPage subjectId={subjectId} classLevel={classLevel} />;
}

export default App;